import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";

import {
  ISoccerMatch,
  ISoccerMatchListItem,
  SoccerMatchStatus,
} from "../../../types";
import { getMatch, getMatches } from "../api";
import { TOURNAMENT_ID } from "../config/tournament";
import { useAppStore } from "../stores/useAppStore";
import { kickoffElapsedClock, liveClock } from "../utils/liveClock";
import { closestKickoff, pollIntervalMs } from "../utils/refreshCadence";
import { useNowTick } from "./useNowTick";

// Slow poll for live matches not currently open in the box-score.
export const BACKGROUND_LIVE_POLL_MS = 4 * 60 * 1000;

/** Australia national team ID from SDAPI */
const AUSTRALIA_TEAM_ID = "bl4fd8cxe6kjvjx8br82gkhnq";
const MAX_MATCHES = 10;
const MAX_FINISHED = 3;

function buildCarousel(allMatches: ISoccerMatchListItem[]): ISoccerMatchListItem[] {
  const live: ISoccerMatchListItem[] = [];
  const played: ISoccerMatchListItem[] = [];
  const fixture: ISoccerMatchListItem[] = [];

  for (const m of allMatches) {
    switch (m.status) {
      case SoccerMatchStatus.PLAYING: live.push(m); break;
      case SoccerMatchStatus.PLAYED: played.push(m); break;
      case SoccerMatchStatus.FIXTURE: fixture.push(m); break;
    }
  }

  // Prioritize Australia's match first among live matches
  if (live.length > 1 && AUSTRALIA_TEAM_ID) {
    live.sort((a, b) => {
      const aAus = a.contestants?.some((c) => c.id === AUSTRALIA_TEAM_ID);
      const bAus = b.contestants?.some((c) => c.id === AUSTRALIA_TEAM_ID);
      return aAus === bAus ? 0 : aAus ? -1 : 1;
    });
  }

  played.sort((a, b) => new Date(b.matchDateTime).getTime() - new Date(a.matchDateTime).getTime());
  const finished = played.slice(0, MAX_FINISHED);
  const rem = MAX_MATCHES - live.length - finished.length;
  const upcoming = rem > 0 ? fixture.slice(0, rem) : [];

  return [...live, ...finished, ...upcoming];
}

export function useScheduleMatches() {
  const matchData = useAppStore((s) => s.matchData);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["schedule-matches", TOURNAMENT_ID],
    queryFn: async () => {
      try {
        return await getMatches({
          tournamentId: TOURNAMENT_ID,
          size: 1000,
          sort: "asc",
          liveScore: true,
        });
      } catch {
        return { matches: [] as ISoccerMatchListItem[] };
      }
    },
    // refetchInterval drives freshness; staleTime just stops re-mounts within
    // the polling window from triggering extra refetches.
    staleTime: 10_000,
    refetchInterval: (q) =>
      pollIntervalMs("matches", closestKickoff(q.state.data?.matches)),
  });

  // Subscribe to the box-score's cache slot for the viewed match — gives us the
  // exact same dataUpdatedAt the box-score uses, so liveClock ticks in lockstep.
  const { dataUpdatedAt: viewedSyncedAt } = useQuery({
    queryKey: [`fixture-${matchData?.id ?? ""}`],
    enabled: false,
  });

  const baseMatches = useMemo(() => (data?.matches ? buildCarousel(data.matches) : []), [data]);

  // Live matches we don't have an active box-score subscriber for. Same
  // `[fixture-${id}]` cache key as useSoccerMatch — only one fetch ever flies.
  const liveBgIds = useMemo(
    () =>
      baseMatches
        .filter((m) => m.status === SoccerMatchStatus.PLAYING && m.id !== matchData?.id)
        .map((m) => m.id),
    [baseMatches, matchData?.id],
  );

  const bgQueries = useQueries({
    queries: liveBgIds.map((id) => ({
      queryKey: [`fixture-${id}`],
      queryFn: () => getMatch({ matchId: id, liveScore: true }),
      refetchInterval: BACKGROUND_LIVE_POLL_MS,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    })),
  });

  const bgMatchById = useMemo(() => {
    const map = new Map<string, { match: ISoccerMatch; syncedAt: number }>();
    liveBgIds.forEach((id, i) => {
      const q = bgQueries[i];
      if (q?.data) map.set(id, { match: q.data, syncedAt: q.dataUpdatedAt });
    });
    return map;
  }, [liveBgIds, ...bgQueries.map((q) => q.dataUpdatedAt)]);

  // Overlay matchData onto the viewed-match row + bg data onto other live rows.
  const matches = useMemo(() => {
    if (!matchData?.id && bgMatchById.size === 0) return baseMatches;
    return baseMatches.map((m) => {
      if (m.id === matchData?.id) {
        return { ...m, status: matchData.status ?? m.status, scores: matchData.scores ?? m.scores };
      }
      const bg = bgMatchById.get(m.id);
      return bg
        ? { ...m, status: bg.match.status ?? m.status, scores: bg.match.scores ?? m.scores }
        : m;
    });
  }, [baseMatches, matchData, bgMatchById]);

  const hasLive = useMemo(
    () => matches.some((m) => m.status === SoccerMatchStatus.PLAYING),
    [matches],
  );

  // Shared 1s heartbeat — same source as MatchStatus, ensures pixel-identical ticks.
  const now = useNowTick(hasLive);

  const liveMinutes = useMemo<Record<string, string>>(() => {
    const next: Record<string, string> = {};
    for (const m of matches) {
      if (m.status !== SoccerMatchStatus.PLAYING) continue;
      // Viewed match: pull from box-score's matchData via the same liveClock formula.
      if (matchData?.id === m.id && matchData.matchDetails?.periodId != null) {
        next[m.id] = liveClock(matchData, viewedSyncedAt, now);
        continue;
      }
      const bg = bgMatchById.get(m.id);
      if (bg?.match.matchDetails?.periodId != null) {
        next[m.id] = liveClock(bg.match, bg.syncedAt, now);
        continue;
      }
      // Other live matches: kickoff-elapsed fallback (list payload has no per-match clock).
      next[m.id] = kickoffElapsedClock(m.matchDateTime, now);
    }
    return next;
  }, [matches, matchData, viewedSyncedAt, bgMatchById, now]);

  return { matches, liveMinutes, isLoading, refetch };
}
